/*
 * Design: Swiss Precision × 和の余白
 * Brand: eightone (#E03030 accent, #1A1A1A headings, Noto Sans JP)
 * UX: スクロール時にヘッダーにシャドウ追加
 * Mobile: フルスクリーンメニュー、タッチターゲット48px、スムーズアニメーション
 */

import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { scrollToContactForm } from "@/hooks/useScrollToContact";

const NAV_ITEMS = [
  { label: "課題", href: "#problems" },
  { label: "ソリューション", href: "#solutions" },
  { label: "サービス", href: "#services" },
  { label: "セミナー", href: "#seminar-section" },
  { label: "支援事例", href: "#cases" },
  { label: "FAQ", href: "#faq" },
];

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#E0E0E0] transition-shadow duration-300 ${
        scrolled ? "shadow-[0_2px_16px_rgba(0,0,0,0.06)]" : ""
      }`}
    >
      <div className="container flex items-center justify-between h-14 sm:h-16 lg:h-20">
        {/* Logo */}
        <a href="/" className="flex items-center gap-2 shrink-0">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#E03030]" />
          <span className="font-bold text-lg tracking-tight text-[#1A1A1A]" style={{ fontFamily: "'Inter', sans-serif" }}>
            eightone
          </span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="text-sm font-medium text-[#333333] hover:text-[#E03030] transition-colors duration-200"
            >
              {item.label}
            </a>
          ))}
        </nav>

        {/* CTA → サイト内 #contact へ */}
        <button
          onClick={() => scrollToContactForm()}
          className="hidden lg:inline-flex items-center px-6 py-2.5 bg-[#E03030] text-white text-sm font-bold rounded hover:bg-[#c72828] transition-colors duration-200"
        >
          無料相談
        </button>

        {/* Mobile Toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden p-3 -mr-2 text-[#1A1A1A] touch-manipulation"
          aria-label="メニュー"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu - Full Screen Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden fixed inset-0 top-14 sm:top-16 bg-white z-40"
          >
            <nav className="container py-6 flex flex-col h-full">
              <div className="flex flex-col gap-1 flex-1">
                {NAV_ITEMS.map((item, i) => (
                  <motion.a
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.05 }}
                    className="text-lg font-medium text-[#333333] py-3.5 px-2 border-b border-[#F0F0F0] hover:text-[#E03030] active:bg-[#F9F9F9] transition-colors touch-manipulation"
                  >
                    {item.label}
                  </motion.a>
                ))}
              </div>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                className="pb-8"
              >
                <button
                  onClick={() => { setMobileOpen(false); scrollToContactForm(); }}
                  className="flex items-center justify-center w-full px-6 py-4 bg-[#E03030] text-white text-base font-bold rounded hover:bg-[#c72828] transition-colors active:scale-[0.98] touch-manipulation"
                >
                  無料相談を予約する
                </button>
                <div className="mt-4 flex items-center justify-center gap-4 text-xs text-[#666666]">
                  <a href="tel:05050521301" className="hover:text-[#E03030] transition-colors" style={{ fontFamily: "'Inter', sans-serif" }}>
                    050-5052-1301
                  </a>
                  <span className="w-px h-3 bg-[#E0E0E0]" />
                  <a href="mailto:81@eightone.co" className="hover:text-[#E03030] transition-colors">
                    81@eightone.co
                  </a>
                </div>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
